import { AlertTriangle, Building2, DollarSign, Siren, Wrench } from 'lucide-react';
import { BREAKDOWN_INCIDENTS, REPAIR_STATUS } from './mockWorkshopData';

function PriorityBadge({ priority }) {
  const styles = {
    critical: 'bg-red-500/15 text-red-700 ring-red-500/30 animate-pulse',
    high: 'bg-orange-500/15 text-orange-700 ring-orange-500/30',
    medium: 'bg-white text-logi-muted ring-logi-grid',
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ring-1 ${styles[priority]}`}
    >
      {priority}
    </span>
  );
}

function RepairStatusBadge({ status }) {
  const config = REPAIR_STATUS[status];
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

export default function BreakdownResponseHub({ incidents = BREAKDOWN_INCIDENTS }) {
  const activeCount = incidents.filter((i) => i.repairStatus !== 'completed').length;
  const totalCost = incidents.reduce((sum, i) => sum + i.costAed, 0);
  const criticalCount = incidents.filter((i) => i.priority === 'critical').length;

  return (
    <section className="overflow-hidden rounded-xl border border-red-500/20 bg-white ring-1 ring-red-500/10">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid bg-red-950/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <Siren className="h-4 w-4 text-red-700" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Breakdown &amp; Incident Response Hub</h3>
            <p className="text-[11px] text-logi-text0">
              {activeCount} active repairs · {criticalCount} critical
            </p>
          </div>
        </div>
        <div className="rounded-xl bg-white px-3 py-2 ring-1 ring-logi-grid">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            Total Cost (Active)
          </p>
          <p className="font-mono text-lg font-bold text-amber-700">
            {totalCost.toLocaleString()} <span className="text-xs">AED</span>
          </p>
        </div>
      </div>

      {criticalCount > 0 && (
        <div className="flex items-center gap-2 border-b border-red-500/20 bg-red-50 px-4 py-2 text-xs text-red-700">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          {criticalCount} critical incident(s) require immediate workshop attention
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead>
            <tr className="border-b border-logi-grid/80 bg-white text-[10px] uppercase tracking-wider text-logi-text0">
              <th className="px-4 py-2.5 font-semibold">Priority</th>
              <th className="px-4 py-2.5 font-semibold">Vehicle No.</th>
              <th className="px-4 py-2.5 font-semibold">Reported Issue</th>
              <th className="px-4 py-2.5 font-semibold">Garage Assignment</th>
              <th className="px-4 py-2.5 font-semibold">Repair Status</th>
              <th className="px-4 py-2.5 font-semibold">Cost (AED)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-logi-grid/30">
            {incidents.map((row) => (
              <tr
                key={row.id}
                className={`transition-colors hover:bg-logi-silver ${
                  row.priority === 'critical' ? 'bg-red-950/10' : ''
                } ${row.repairStatus === 'completed' ? 'opacity-70' : ''}`}
              >
                <td className="px-4 py-2.5">
                  <PriorityBadge priority={row.priority} />
                </td>
                <td className="px-4 py-2.5">
                  <span className="font-mono font-semibold text-logi-accent">
                    {row.vehicleNumber}
                  </span>
                  <p className="text-[10px] text-logi-text0">{row.reportedAt}</p>
                </td>
                <td className="max-w-[240px] px-4 py-2.5">
                  <span className="flex items-start gap-1.5 text-xs leading-relaxed text-logi-muted">
                    <Wrench className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-logi-text0" />
                    {row.reportedIssue}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-xs text-logi-muted">
                    <Building2 className="h-3.5 w-3.5 text-logi-text0" />
                    {row.garage}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <RepairStatusBadge status={row.repairStatus} />
                </td>
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-1 font-mono text-sm font-semibold text-amber-700/90">
                    <DollarSign className="h-3.5 w-3.5 opacity-60" />
                    {row.costAed.toLocaleString()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
